import { useMemo } from 'react';
import { Column } from 'react-table';
import { Table, TableState } from '@nbirl/nbi-common-ui-libs';

import './document-list-table.css';

import { useCurrentUser } from '../../../../../../shared/context';
import { Permission } from '../../../../../../shared/enum';
import { TableQueryState } from '../../../../../../shared/models';
import { DocumentListRow } from '../../../models';
import { getDocumentRowProps } from '../../../utils';
import { BulkActionsHeader } from './bulk-actions-header';
import { DocumentsListEmptyState } from './documents-list-empty-state.component';

type DocumentsListTableProps = {
  columns: Column<DocumentListRow>[];
  data: DocumentListRow[];
  isLoading: boolean;
  isSearching: boolean;
  totalCount: number;
  pageCount: number;
  selectedRowIds: Record<string, boolean>;
  hasSharedDocument?: boolean;
  tableState?: TableQueryState;
  showBulkActions?: boolean;
  error?: unknown;
  emptyState?: React.ReactNode;
  onStateChange: (state: TableState) => void;
  onDeselectRows: () => void;
  onBulkStatusChange: () => void;
};

export const DocumentsListTable: React.FC<DocumentsListTableProps> = ({
  columns,
  data,
  isLoading,
  isSearching,
  totalCount,
  pageCount,
  hasSharedDocument = false,
  selectedRowIds,
  tableState,
  error,
  emptyState,
  onStateChange,
  onDeselectRows,
  onBulkStatusChange,
}) => {
  const user = useCurrentUser();

  const hiddenColumns = user.permissions.includes(Permission.ViewDocumentStatus)
    ? []
    : ['status'];

  const selectedRows = useMemo(
    () => data.filter((row) => selectedRowIds[row.id]),
    [data, selectedRowIds]
  );

  const showBulkActions = Object.keys(selectedRowIds).length > 0;

  return (
    <Table
      isLoading={isLoading}
      error={error}
      getRowProps={getDocumentRowProps}
      columns={columns}
      data={data}
      {...tableState}
      totalCount={totalCount}
      pageCount={pageCount}
      showPagination={isSearching}
      hiddenColumns={hiddenColumns}
      enableSelection
      selectedRowIds={selectedRowIds}
      isRowSelectable={() => !hasSharedDocument}
      headerRenderer={
        showBulkActions
          ? (headerGroups) => (
              <BulkActionsHeader
                headerGroups={headerGroups}
                selectedRows={selectedRows}
                onDeselectRows={onDeselectRows}
                onBulkStatusChange={onBulkStatusChange}
              />
            )
          : undefined
      }
      disableSortRemove
      hasRowDividers
      getRowId={(row: DocumentListRow) => row.id}
      emptyState={
        emptyState || <DocumentsListEmptyState isSearching={isSearching} />
      }
      onStateChange={onStateChange}
    />
  );
};
